// src/pages/SettingsPage.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  View,
  Flex,
  Heading,
  Text,
  Card,
  Button,
  Alert,
  Divider,
  Loader,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
} from "@aws-amplify/ui-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";

import { gql } from "../lib/gqlClient";
import * as mutations from "../graphql/mutations";
import * as queries from "../graphql/queries";
import { normalizeGraphQLError } from "../utils/graphqlErrors";
import { useAdminTenant } from "../admin/AdminTenantContext";
import AdminTenantRequired from "../components/admin/AdminTenantRequired";
import SimpleDialog from "../components/common/SimpleDialog";
import { getFieldError } from '../utils/formErrors';
import { normalizeUsCanadaPhone } from "../utils/phone";
import { SwitchField } from "@aws-amplify/ui-react";
import { fetchUserAttributes } from "aws-amplify/auth";

// --- small helpers ---------------------------------------------------------

function isValidEmail(email) {
  if (!email) return false;
  const s = String(email).trim();
  return s.includes("@") && s.includes(".") && !s.includes(" ");
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

async function safeCopy(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function isExpired(expiresAtIso) {
  const t = Date.parse(expiresAtIso);
  if (Number.isNaN(t)) return false; // if malformed, don't hide
  return t <= Date.now();
}

// --- Page ------------------------------------------------------------------

export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const flash = location.state?.flash;

  const { authStatus, user } = useAuthenticator((ctx) => [ctx.authStatus, ctx.user]);

  const { tenantKey, isPlatformAdmin } = useAdminTenant();

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteTouched, setInviteTouched] = useState(false);

  // Page state
  const [pageError, setPageError] = useState("");
  const [pageSuccess, setPageSuccess] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Invites list
  const [invites, setInvites] = useState([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);

  const [tenant, setTenant] = useState(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);

  const [form, setForm] = useState({ name: "" });
  const [touched, setTouched] = useState({});

  const [errors, setErrors] = useState({}); // server errors keyed by field
  const [isSavingOrg, setIsSavingOrg] = useState(false);

  // User profile (notifications prefs)
  const [userSub, setUserSub] = useState("");
  const [myTenantKey, setMyTenantKey] = useState(""); 
  const loginEmail = user?.signInDetails?.loginId || user?.attributes?.email || "";

  const [profile, setProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [profileForm, setProfileForm] = useState({
    alertEmail: "",
    phoneE164: "",
    smsCriticalEnabled: false,
  });
  const [profileTouched, setProfileTouched] = useState({});
  const [profileErrors, setProfileErrors] = useState({});

  const pageMessageRef = useRef(null);

  // your accept flow is login (invite param triggers invite-aware form)
  const inviteAcceptBaseUrl = useMemo(() => {
    const origin = window?.location?.origin || "";
    return `${origin}/login`;
  }, []);

  const emailError = useMemo(() => {
    if (!inviteTouched) return "";
    if (!inviteEmail.trim()) return "Email is required.";
    if (!isValidEmail(inviteEmail)) return "Enter a valid email address.";
    return "";
  }, [inviteEmail, inviteTouched]);

  const canSend = !emailError && inviteEmail.trim() && !isSending;

  function clearFlash() {
    navigate(location.pathname, { replace: true, state: {} });
  }

  function resetInviteForm() {
    setInviteEmail("");
    setInviteTouched(false);
    setInviteError("");
    setInviteSuccess("");
  }

  function closeInviteModal() {
    setShowInviteModal(false);
    setIsSending(false);
    resetInviteForm();
  }

  function inviteLinkFromCode(code) {
    return `${inviteAcceptBaseUrl}?invite=${encodeURIComponent(code)}`;
  }

  useEffect(() => {
    let cancelled = false;

    async function loadIdentityBits() {
      if (authStatus !== "authenticated") return;

      const attrs = await fetchUserAttributes();
      const sub = attrs?.sub || "";
      const myTk = attrs?.["custom:tenantKey2"] || "";

      if (!cancelled) {
        setUserSub(sub);
        setMyTenantKey(myTk);
      }
        
    }

    loadIdentityBits().catch(console.error);
    return () => { cancelled = true; };
  }, [authStatus]);

  // ---- Load invites --------------------------------------------------------

  async function loadInvites() {
    setIsLoadingInvites(true);
    setPageError("");

    try {
      const res = await gql(queries.invitesByTenant, {
        tenantKey,
        sortDirection: "DESC",
        limit: 50,
      });

      const items = res?.invitesByTenant?.items ?? [];
      const normalized = Array.isArray(items) ? items.filter(Boolean) : [];

      const pending = normalized.filter(
        (x) => 
          (x.status || "PENDING") === "PENDING" &&
          !isExpired(x?.expiresAt)
      );

      setInvites(pending);
    } catch (err) {
      const n = normalizeGraphQLError(err);
      setPageError(n?.message || "Failed to load invites.");
    } finally {
      setIsLoadingInvites(false);
    }
  }

  const canEditAccount = !isPlatformAdmin || (tenantKey && myTenantKey && tenantKey === myTenantKey);

  useEffect(() => {
    if (!tenantKey) return;
    loadInvites();
    loadTenant();
  }, [tenantKey]);

  useEffect(() => {
    if (!tenantKey || !userSub) return;

    if (!canEditAccount) {
      // Optional: clear out the account section state to avoid confusion
      setProfile(null);
      setIsLoadingProfile(false);
      return;
    }

    loadUserProfile();
  }, [tenantKey, userSub, canEditAccount]);

  // Patch email field for legacy users without userprofile.
  useEffect(() => {
    if (!loginEmail) return;

    setProfileForm((p) => ({
      ...p,
      alertEmail: p.alertEmail?.trim() ? p.alertEmail : loginEmail,
    }));
  }, [loginEmail]);

  // ---- Create invite -------------------------------------------------------

  async function onSendInvite() {
    setInviteTouched(true);
    setInviteSuccess("");
    setInviteError("");
    setPageSuccess("");

    const email = inviteEmail.trim().toLowerCase();
    if (!email || emailError) return;

    setIsSending(true);

    try {
      const res = await gql(mutations.addInvite, {tenantKey, 
        input: {
          email: email,
        },
      });

      setPageSuccess(`Invite sent to ${email}.`);
      await loadInvites();
      closeInviteModal();
    } catch (err) {
      console.error("invite error", err);
      const n = normalizeGraphQLError(err);
      setInviteError(n?.message || "Failed to send invite.");
    } finally {
      setIsSending(false);
    }
  }

  // ---- Copy / revoke -------------------------------------------------------

  async function onCopyInvite(invite) {
    setPageError("");
    setPageSuccess("");

    const code = invite?.code;
    if (!code) {
      setPageError("This invite doesn’t include a code to copy.");
      return;
    }

    const ok = await safeCopy(inviteLinkFromCode(code));
    if (!ok) setPageError("Couldn’t copy to clipboard in this browser.");
    else setPageSuccess("Invite link copied.");
  }

  async function onRevokeInvite(invite) {
    if (!invite?.id) return;
    setPageError("");
    setPageSuccess("");

    try {
      // Use CRUD updateInvite; no custom revoke mutation needed right now.
      await gql(mutations.updateInvite, {
        input: {
          id: invite.id,
          status: "REVOKED",
        },
      });

      setPageSuccess("Invite revoked.");
      await loadInvites();
    } catch (err) {
      const n = normalizeGraphQLError(err);
      setPageError(n?.message || "Failed to revoke invite.");
    }
  }

  const validation = useMemo(() => {
    const v = {};
    const name = String(form.name || "").trim();

    if (!name) v.name = "Organization name is required.";
    else if (name.length > 80) v.name = "Keep it under 80 characters.";
    else if (name.length < 2) v.name = "Must be more than 1 character long.";

    return v;
  }, [form.name]);

  const profileValidation = useMemo(() => {
    const v = {};
    const email = String(profileForm.alertEmail || "").trim();
    const phone = String(profileForm.phoneE164 || "").trim();

    if (!email) v.alertEmail = "Email is required.";
    else if (!isValidEmail(email)) v.alertEmail = "Enter a valid email address.";

    if (profileForm.smsCriticalEnabled) {
      if (!phone) v.phoneE164 = "Phone is required for SMS alerts.";
      else if (!normalizeUsCanadaPhone(phone)) v.phoneE164 = "Enter a valid US/Canada phone number.";
    } else {
      if (phone && !normalizeUsCanadaPhone(phone)) v.phoneE164 = "Enter a valid US/Canada phone number.";
    }

    return v;
  }, [profileForm]);

  const nameError = getFieldError({
    field: "name",
    touched,
    validation,
    errors,
  });

  const alertEmailError = getFieldError({
    field: "alertEmail",
    touched: profileTouched,
    validation: profileValidation,
    errors: profileErrors,
  });

  const phoneError = getFieldError({
    field: "phoneE164",
    touched: profileTouched,
    validation: profileValidation,
    errors: profileErrors,
  });


  const canSaveOrg =
    !nameError &&
    String(form.name || "").trim() &&
    !isSavingOrg;

  const canSaveProfile =
    !alertEmailError &&
    !phoneError &&
    String(profileForm.alertEmail || "").trim() &&
    !isSavingProfile;

  async function loadTenant() {
    if (!tenantKey) return;

    setIsLoadingTenant(true);
    setPageError("");
    setErrors({});

    try {
      const res = await gql(queries.getTenant, { id: tenantKey });
      const t = res?.getTenant ?? null;

      setTenant(t);
      setForm({ name: t?.name ?? "" });
      setTouched({});
    } catch (err) {
      const n = normalizeGraphQLError(err);
      setPageError(n.global || "Failed to load organization.");
    } finally {
      setIsLoadingTenant(false);
    }
  }

  async function onSaveOrgName() {
    setTouched((prev) => ({ ...prev, name: true }));
    setPageSuccess("");
    setPageError("");
    setErrors({});

    // block if client-side invalid
    if (validation.name) return;

    setIsSavingOrg(true);
    try {
      const name = String(form.name || "").trim();

      if (!tenant) {
        // first-time creation (legacy user or migration)
        const res = await gql(mutations.createTenant, {
          input: {
            id: tenantKey,
            name,
          },
        });

        const created = res?.createTenant ?? { id: tenantKey, name };
        setTenant(created);
        setForm({ name: created.name ?? name });
        setPageSuccess("Organization created.");
        return;
      }


      const res = await gql(mutations.updateTenant, {
        input: { id: tenantKey, name },
      });

      const updated = res?.updateTenant ?? { ...(tenant || {}), id: tenantKey, name };
      setTenant(updated);
      setForm({ name: updated.name ?? name });

      setPageSuccess("Organization name updated.");
    } catch (err) {
      const n = normalizeGraphQLError(err);
      if (n.global) setPageError(n.global);
      setErrors(n);
    } finally {
      setIsSavingOrg(false);
    }
  }

  async function loadUserProfile() {
    if (!tenantKey || !userSub) return;

    setIsLoadingProfile(true);
    setProfileErrors({});
    setPageError("");

    const id = `${tenantKey}:${userSub}`;

    try {
      const res = await gql(queries.getUserProfile, { id });
      const p = res?.getUserProfile ?? null;

      setProfile(p);

      console.log("phone field from profile: " + p?.phoneE164);
      setProfileForm({
        alertEmail: p?.alertEmail ?? "",
        phoneE164: p?.phoneE164 ?? "",
        smsCriticalEnabled: !!p?.smsCriticalEnabled,
      });

      setProfileTouched({});
    } catch (err) {
      const n = normalizeGraphQLError(err);
      setPageError(n.global || "Failed to load account settings.");
    } finally {
      setIsLoadingProfile(false);
    }
  }

  async function onSaveAccount() {
    if (!canEditAccount) {
      setPageError("Account settings are not available while acting as another tenant.");
      return;
    }
    setProfileTouched((p) => ({ ...p, alertEmail: true, phoneE164: true }));
    setProfileErrors({});
    setPageError("");
    setPageSuccess("");

    if (profileValidation.alertEmail || profileValidation.phoneE164) return;

    setIsSavingProfile(true);

    const normalizedPhone = normalizeUsCanadaPhone(profileForm.phoneE164);

    const inputBase = {
      alertEmail: String(profileForm.alertEmail).trim().toLowerCase(),
      phoneE164: normalizedPhone,
      smsCriticalEnabled: !!profileForm.smsCriticalEnabled,
    };

    try {
      const res = await gql(mutations.upsertUserProfile, {input: inputBase});
      const created = res?.upsertUserProfile ?? inputBase;
      setProfile(created);
      setProfileForm({
        alertEmail: created?.alertEmail ?? "",
        phoneE164: created?.phoneE164 ?? "",
        smsCriticalEnabled: !!created?.smsCriticalEnabled,
      });
      setPageSuccess("Account settings saved.");
    } catch (err) {
      const n = normalizeGraphQLError(err);
      if (n.global) setPageError(n.global);
      setProfileErrors(n);
    } finally {
      setIsSavingProfile(false);
    }
  }

  useEffect(() => {
    if (flash || pageSuccess || pageError) {
      pageMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [flash, pageSuccess, pageError]);

  // -------------------------------------------------------------------------

  if (isPlatformAdmin && !tenantKey) {
    return (
      <AdminTenantRequired
        title="View/edit Settings"
        message="Select a tenant in the top bar before viewing/editing settings."
      />
    );
  }
  return (
    <View maxWidth="60rem" margin="0 auto" padding="1.5rem" width="100%">
      <Flex direction="column" gap="1rem" width="100%">
        <Heading level={2}>Settings</Heading>

        {flash && (
          <Alert
            ref={pageMessageRef}
            variation="success"
            hasIcon
            isDismissible
            onDismiss={clearFlash}
          >
            {flash}
          </Alert>
        )}

        {pageSuccess && (
          <Alert
            ref={pageMessageRef}
            variation="success"
            hasIcon
            isDismissible
            onDismiss={() => setPageSuccess("")}
          >
            {pageSuccess}
          </Alert>
        )}

        {pageError && (
          <Alert
            ref={pageMessageRef}
            variation="error"
            hasIcon
            isDismissible
            onDismiss={() => setPageError("")}
          >
            {pageError}
          </Alert>
        )}

        {/* Organization */}
        <Card variation="elevated" width="100%">
          <Flex direction="column" gap="0.75rem">
            {/* Header like Account card */}
            <Heading level={4}>Organization</Heading>

            {/* Org name field (normal field) */}
            {!tenantKey ? (
              <Text color="font.secondary">Loading session…</Text>
            ) : isLoadingTenant ? (
              <Flex alignItems="center" gap="0.75rem">
                <Loader size="small" />
                <Text>Loading organization…</Text>
              </Flex>
            ) : (
              <>
                <View maxWidth="32rem" width="100%">
                  <TextField
                    label="Organization name"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                    placeholder="Acme Property Management"
                    errorMessage={nameError || ""}
                    hasError={!!nameError}
                  />

                  <Flex justifyContent="flex-end" marginTop="0.5rem">
                    <Button
                      size="small"
                      variation="primary"
                      onClick={onSaveOrgName}
                      isLoading={isSavingOrg}
                      isDisabled={!canSaveOrg}
                    >
                      Save
                    </Button>
                  </Flex>
                </View>
              </>
            )}

            {/* Divider between org fields and invites */}
            <Divider />

            {/* Invites section (button + pending invites are grouped; no divider between them) */}
            <Flex direction="column" gap="0.5rem">
              <Heading level={5} margin="0">
                Teammates
              </Heading>

              <Button
                variation="primary"
                size="small"
                alignSelf="flex-start"
                onClick={() => {
                  setInviteError("");
                  setInviteSuccess("");
                  setShowInviteModal(true);
                }}
              >
                Invite teammate
              </Button>
            </Flex>


            <Text color="font.secondary">
              Invite teammates to join your organization.
            </Text>

            <Flex direction="column" gap="0.5rem">
              <Flex alignItems="center" justifyContent="space-between">
                <Heading level={5} margin="0">
                  Pending invites
                </Heading>
              </Flex>

              {!tenantKey ? (
                <Text color="font.secondary">Loading session…</Text>
              ) : isLoadingInvites ? (
                <Flex alignItems="center" gap="0.75rem">
                  <Loader size="small" />
                  <Text>Loading invites…</Text>
                </Flex>
              ) : invites.length === 0 ? (
                <Text color="font.secondary">No pending invites.</Text>
              ) : (
                <View overflow="auto">
                  <Table highlightOnHover={true}>
                    <TableHead>
                      <TableRow>
                        <TableCell as="th">Email</TableCell>
                        <TableCell as="th">Created</TableCell>
                        <TableCell as="th">Expires</TableCell>
                        <TableCell as="th" textAlign="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invites.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>{inv.emailLower || "—"}</TableCell>
                          <TableCell>{formatDateTime(inv.createdAt)}</TableCell>
                          <TableCell>{formatDateTime(inv.expiresAt)}</TableCell>
                          <TableCell>
                            <Flex justifyContent="flex-end" gap="0.5rem">
                              <Button
                                size="small"
                                variation="link"
                                onClick={() => onCopyInvite(inv)}
                              >
                                Copy link
                              </Button>
                              <Button
                                size="small"
                                variation="link"
                                onClick={() => onRevokeInvite(inv)}
                              >
                                Revoke
                              </Button>
                            </Flex>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </View>
              )}
            </Flex>
          </Flex>
        </Card>


        {/* Account placeholder */}
        {!canEditAccount ? (
          <Card variation="elevated" width="100%">
            <Flex direction="column" gap="0.75rem">
              <Heading level={4}>Account</Heading>
                <Text color="font.secondary">
                  Account settings (alert email / phone) are tied to your user identity and don’t load while you’re acting as a different tenant.
                </Text>
            </Flex>
          </Card>
        ) : (
          <Card variation="elevated" width="100%">
            <Flex direction="column" gap="0.75rem">
              <Heading level={4}>Account</Heading>

              {authStatus !== "authenticated" ? (
                <Text color="font.secondary">Loading session…</Text>
              ) : isLoadingProfile ? (
                <Flex alignItems="center" gap="0.75rem">
                  <Loader size="small" />
                  <Text>Loading account settings…</Text>
                </Flex>
              ) : (
                <>
                  <Flex direction="column" gap="1rem" maxWidth="32rem" width="100%">
                    <TextField
                      label="Alert email"
                      value={profileForm.alertEmail}
                      onChange={(e) =>
                        setProfileForm((p) => ({ ...p, alertEmail: e.target.value }))
                      }
                      onBlur={() => setProfileTouched((p) => ({ ...p, alertEmail: true }))}
                      placeholder="name@company.com"
                      errorMessage={alertEmailError || ""}
                      hasError={!!alertEmailError}
                    />

                    <TextField
                      label="Phone number"
                      value={profileForm.phoneE164}
                      onChange={(e) =>
                        setProfileForm((p) => ({ ...p, phoneE164: e.target.value }))
                      }
                      onBlur={() => setProfileTouched((p) => ({ ...p, phoneE164: true }))}
                      placeholder="202-555-0123"
                      errorMessage={phoneError || ""}
                      hasError={!!phoneError}
                      descriptiveText="US/Canada only."
                    />

                    <Divider marginTop="1rem" marginBottom="1rem"/>

                    <Flex direction="column" gap="0.5rem" marginTop="0.25rem">
                      <Heading level={5} margin="0">
                        Notifications
                      </Heading>
                      <SwitchField
                        label="SMS for critical alerts (e.g., leak detected, device offline)"
                        isChecked={!!profileForm.smsCriticalEnabled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setProfileForm((p) => ({
                            ...p,
                            smsCriticalEnabled: checked,
                          }));
                          // show validation immediately if they enable SMS with no phone
                          if (checked) setProfileTouched((t) => ({ ...t, phoneE164: true }));
                        }}
                        labelPosition="end"
                      />
                      <Text fontSize="0.75rem" 
                      color="font.tertiary"
                      lineHeight="1.2">
                        By enabling SMS, you consent to receive automated <b>transactional</b> alerts from <b>Plex LD</b> (leak/offline).
                        Msg frequency varies. Msg &amp; data rates may apply. Reply <b>STOP</b> to opt out, <b>HELP</b> for help.{" "}
                        <a href="https://www.plexld.com/terms" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
                          Terms
                        </a>{" "}
                        •{" "}
                        <a href="https://www.plexld.com/privacy" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
                          Privacy
                        </a>
                      </Text>
                    </Flex>
                    <Flex justifyContent="flex-end" marginTop="0.25rem">
                      <Button
                        size="small"
                        variation="primary"
                        onClick={onSaveAccount}
                        isLoading={isSavingProfile}
                        isDisabled={!canSaveProfile}
                      >
                        Save
                      </Button>
                    </Flex>
                  </Flex>
                </>
              )}
            </Flex>
          </Card>
        )}
      </Flex>

      {/* Invite modal (simple backdrop modal for speed) */}
      {showInviteModal && (
        <SimpleDialog
          open={showInviteModal}
          onClose={closeInviteModal}
          title="Invite a teammate"
          maxWidth="36rem"
        >
          <Flex direction="column" gap="0.75rem">
            <Text color="font.secondary">
              We’ll email them an invite link to join your organization.
            </Text>

            {inviteSuccess && (
              <Alert
                variation="success"
                hasIcon
                isDismissible
                onDismiss={() => setInviteSuccess("")}
              >
                {inviteSuccess}
              </Alert>
            )}

            {inviteError && (
              <Alert
                variation="error"
                hasIcon
                isDismissible
                onDismiss={() => setInviteError("")}
              >
                {inviteError}
              </Alert>
            )}
            <TextField
              label="Email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onBlur={() => setInviteTouched(true)}
              placeholder="name@company.com"
              errorMessage={emailError}
              hasError={!!emailError}
            />

            <Flex justifyContent="flex-end" gap="0.5rem">
              <Button variation="link" onClick={closeInviteModal}>
                Cancel
              </Button>
              <Button
                variation="primary"
                onClick={onSendInvite}
                isLoading={isSending}
                isDisabled={!canSend}
              >
                Send invite
              </Button>
            </Flex>
          </Flex>
        </SimpleDialog>
      )}
    </View>
  );
}
